import React, { useState, useEffect } from "react";
import TableRow from "./TableRow";
import "./HierarchicalTable.css";

const HierarchicalTable = ({ data }) => {
  const [rows, setRows] = useState([]);
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    // Initialize rows and store original values
    const initializeData = (items) => {
      const values = {};
      const processRow = (row) => {
        values[row.id] = row.value;
        if (row.children) {
          row.children.forEach(processRow);
        }
      };
      items.forEach(processRow);
      return values;
    };

    const originals = initializeData(data.rows);
    setOriginalValues(originals);
    setRows(data.rows);
  }, [data]);

  // Calculate sum of children
  const calculateChildrenSum = (children) => {
    return children.reduce((sum, child) => sum + child.value, 0);
  };

  // Calculate grand total
  const calculateGrandTotal = () => {
    return rows.reduce((sum, row) => sum + row.value, 0);
  };

  // Update a specific row by id
  const updateRowValue = (id, newValue, isPercentage = false) => {
    const updateRows = (items) => {
      return items.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row };

          if (row.children && row.children.length > 0) {
            // Parent row - distribute to children
            const currentTotal = calculateChildrenSum(row.children);
            const distributionFactor = newValue / currentTotal;

            updatedRow.children = row.children.map((child) => ({
              ...child,
              value: child.value * distributionFactor,
            }));

            updatedRow.value = newValue;
          } else {
            // Leaf node
            if (isPercentage) {
              updatedRow.value = row.value * (1 + newValue / 100);
            } else {
              updatedRow.value = newValue;
            }
          }

          return updatedRow;
        } else if (row.children) {
          // Check children and update parent sum
          const updatedChildren = updateRows(row.children);
          const childrenSum = calculateChildrenSum(updatedChildren);

          return {
            ...row,
            children: updatedChildren,
            value: childrenSum,
          };
        }

        return row;
      });
    };

    setRows(updateRows(rows));
  };

  // Calculate variance percentage
  const calculateVariance = (currentValue, originalValue) => {
    if (originalValue === 0) return 0;
    return ((currentValue - originalValue) / originalValue) * 100;
  };

  const renderRows = (items, level = 0) => {
    return items.map((row) => (
      <TableRow
        key={row.id}
        row={row}
        level={level}
        originalValue={originalValues[row.id]}
        onUpdate={updateRowValue}
        calculateVariance={calculateVariance}
        renderChildren={
          row.children ? () => renderRows(row.children, level + 1) : null
        }
      />
    ));
  };

  const grandTotal = calculateGrandTotal();
  const originalGrandTotal = Object.values(originalValues).reduce(
    (sum, val) => sum + val,
    0
  );
  const grandTotalVariance = calculateVariance(grandTotal, originalGrandTotal);

  return (
    <div className="hierarchical-table-container">
      <h1>Hierarchical Table - Budget Allocation</h1>
      <table className="hierarchical-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Value</th>
            <th>Input</th>
            <th>Allocation %</th>
            <th>Allocation Val</th>
            <th>Variance %</th>
          </tr>
        </thead>
        <tbody>
          {renderRows(rows)}
          <tr className="grand-total-row">
            <td>
              <strong>Grand Total</strong>
            </td>
            <td>
              <strong>{grandTotal.toFixed(2)}</strong>
            </td>
            <td></td>
            <td></td>
            <td></td>
            <td>
              <strong>{grandTotalVariance.toFixed(2)}%</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default HierarchicalTable;
